import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    useListCustomersTodos,
    useCreateActivity,
    useUpdateActivity,
    CreateCustomerActivityInput,
    UpdateCustomerActivityInput,
    Activity,
    useListCustomers,
    ListCustomerOptions,
} from "@/lib/api/customer";
import { useListMinimalUsers, DbUserWithPackages } from "@/lib/api/users";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { Edit2, Save, X, Plus } from "lucide-react";
import { CommonDestinationTimezone } from "@/lib/api/document";
import { useStytchMember } from "@stytch/react/b2b";
import { getUserDbId } from "@/lib/models/auth";
import { Loader } from "@/components/ui/loader";
import { useQueryParamArray } from "@/hooks/use-query-param-array";

function renderPagination(
    currentPage: number,
    totalPages: number,
    setPage: (page: number) => void
) {
    return (
        <div className="flex items-center justify-between mt-4">
            <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage <= 1}
            >
                Previous
            </Button>
            <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
            </span>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage >= totalPages}
            >
                Next
            </Button>
        </div>
    );
}

// Extended Activity type that includes Customer data from API
type ActivityWithCustomer = Activity & {
    Customer?: { long_name: string };
    last_completed_at?: string;
    last_completed_by_id?: string;
    last_assigned_at?: string;
    last_updated_by_id?: string;
};

interface CustomerNotesSectionProps {
    customerId?: string;
    showCustomerName?: boolean;
    useAccordion?: boolean;
    customerListFilter?: ListCustomerOptions;
    activity_type?: "risk" | "sales_notes";
}

export function CustomerNotesSection({
    customerId,
    showCustomerName,
    customerListFilter,
    activity_type,
    useAccordion = false,
}: CustomerNotesSectionProps) {
    const { toast } = useToast();
    const { member } = useStytchMember();
    const currentUserId = member ? getUserDbId(member) : "";
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const minimalCustomersListResult = useListCustomers({ ...(customerListFilter || { minimal: true, hasRiskProfile: true }) });

    const minimalUsersListResult = useListMinimalUsers();
    const notesQueryResult = useListCustomersTodos({
        customer_type: customerListFilter?.customer_type,
        entity_type: "NOTE",
        customer_id: customerId,
        page,
        pageSize: 10,
    });

    const createActivity = useCreateActivity();
    const updateActivity = useUpdateActivity();

    const minimalCustomersList = minimalCustomersListResult?.data?.data || [];
    const minimalUsersList = minimalUsersListResult?.data?.data || [];
    const notes: ActivityWithCustomer[] = notesQueryResult.data?.data?.data || [];
    const pagination = notesQueryResult.data?.data?.pagination;
    const isLoading = notesQueryResult.isLoading;

    const mostRecentNote = notes.length > 0 ? notes[0] : null;
    const olderNotes = notes.length > 1 ? notes.slice(1) : [];

    if (isLoading && page === 1) {
        return (
            <div className="flex items-center justify-center p-4">
                <Loader />
                <span className="ml-2 text-sm text-muted-foreground">Loading notes...</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold">Notes</h3>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsCreating(true)}
                    disabled={isCreating}
                >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Note
                </Button>
            </div>

            {isCreating && (
                <NoteForm
                    customerId={customerId}
                    allowCustomerSelection={!customerId}
                    onSave={() => {
                        setIsCreating(false);
                        setPage(1);
                    }}
                    onCancel={() => setIsCreating(false)}
                    currentUserId={currentUserId}
                    customersList={minimalCustomersList}
                    activity_type={activity_type}
                />
            )}

            {notes.length === 0 && !isCreating ? (
                <div className="text-center py-8 text-muted-foreground">
                    No notes found for this customer
                </div>
            ) : useAccordion ? (
                <>
                    {mostRecentNote && (
                        <NoteCard
                            note={mostRecentNote}
                            isEditing={editingId === mostRecentNote.id}
                            onEdit={() => setEditingId(mostRecentNote.id)}
                            onCancelEdit={() => setEditingId(null)}
                            onSave={() => {
                                setEditingId(null);
                                setPage(1);
                            }}
                            users={minimalUsersList}
                            showCustomerName={showCustomerName}
                            activity_type={activity_type}
                        />
                    )}

                    {olderNotes.length > 0 && (
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="older-notes" className="">
                                <AccordionTrigger className="px-4 hover:no-underline">
                                    <span className="text-sm font-medium">
                                        View older notes ({olderNotes.length}
                                        {pagination && pagination.totalCount > notes.length && `+ more`})
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent>
                                        <div className="space-y-3 p-4 max-h-[450px] overflow-y-scroll">
                                            {olderNotes.map((note) => (
                                                <NoteCard
                                                    key={note.id}
                                                    note={note}
                                                    isEditing={editingId === note.id}
                                                    onEdit={() => setEditingId(note.id)}
                                                    onCancelEdit={() => setEditingId(null)}
                                                    onSave={() => {
                                                        setEditingId(null);
                                                        setPage(1);
                                                    }}
                                                    users={minimalUsersList}
                                                    showCustomerName={showCustomerName}
                                                    activity_type={activity_type}
                                                />
                                            ))}
                                            {pagination && (
                                                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setPage(Math.max(page - 1, 1))}
                                                        disabled={page <= 1}
                                                    >
                                                        Previous
                                                    </Button>
                                                    <span className="text-sm text-muted-foreground">
                                                        Page {page} of {pagination.totalPages}
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setPage(Math.min(page + 1, pagination.totalPages))}
                                                        disabled={page >= pagination.totalPages}
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    )}
                </>
            ) : (
                <>
                    <div className="space-y-3">
                        {notes.map((note) => (
                            <NoteCard
                                key={note.id}
                                note={note}
                                isEditing={editingId === note.id}
                                onEdit={() => setEditingId(note.id)}
                                onCancelEdit={() => setEditingId(null)}
                                onSave={() => {
                                    setEditingId(null);
                                    setPage(1);
                                }}
                                users={minimalUsersList}
                                showCustomerName={showCustomerName}
                                activity_type={activity_type}
                            />
                        ))}
                    </div>
                    {pagination && renderPagination(
                        pagination.currentPage,
                        pagination.totalPages,
                        setPage
                    )}
                </>
            )}
        </div>
    );
}

interface NoteFormProps {
    customerId?: string;
    note?: ActivityWithCustomer;
    onSave: () => void;
    onCancel: () => void;
    currentUserId: string;
    onCustomerChange?: (customerId: string) => void;
    allowCustomerSelection?: boolean;
    customersList?: { id: string; long_name: string }[];
    activity_type?: "risk" | "sales_notes";
}

function NoteForm({ customerId: propCustomerId, customersList, note, onSave, onCancel, onCustomerChange, allowCustomerSelection, activity_type }: NoteFormProps) {
    const [customerId, setCustomerId] = useState(propCustomerId || note?.customer_id);
    const [content, setContent] = useState(note?.content || "");
    const [isSaving, setIsSaving] = useState(false);
    const { toast } = useToast();
    const createActivity = useCreateActivity();
    const updateActivity = useUpdateActivity();

    const handleCustomerChange = (value: string) => {
        setCustomerId(value);
        if (onCustomerChange) {
            onCustomerChange(value);
        }
    };

    const handleSave = async () => {
        if (!content.trim() || isSaving) return;

        setIsSaving(true);
        try {
            if (note) {
                const updateData = UpdateCustomerActivityInput.safeParse({
                    id: note.id,
                    resource_type: "NOTE",
                    customer_id: customerId,
                    content,
                    activity_type: activity_type,
                });

                if (!updateData.success) {
                    toast({ title: "Invalid data", description: updateData.error.message });
                    return;
                }

                const result = await updateActivity(updateData.data);
                if (!result.success) throw result;
            } else {
                const createData = CreateCustomerActivityInput.safeParse({
                    resource_type: "NOTE",
                    customer_id: customerId,
                    content,
                    activity_type: activity_type,
                });

                if (!createData.success) {
                    toast({ title: "Invalid data", description: createData.error.message });
                    return;
                }

                const result = await createActivity(createData.data);
                if (!result.success) throw result;
            }

            onSave();
        } catch (error) {
            console.error("Failed to save note:", error);
            toast({ title: "Failed to save note" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card>
            <CardContent className="p-4">
                <div className="grid gap-2">
                    <div className="flex flex-col gap-2">
                    {allowCustomerSelection && !note && (
                        <Combobox
                            id="customerFilter"
                            dwimOnChange
                            virtualizeList
                            options={customersList?.map((item: any) => ({ value: item.id, label: item.long_name })) || []}
                            value={customerId || ""}
                            onValueChange={handleCustomerChange}
                            placeholder="Select Customer"
                            className=""
                            popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                        />
                    )}
                        <div className="flex">
                                                <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        className="flex-1"
                        rows={9}
                        placeholder="Enter your note..."
                        autoFocus
                    />
                    <div className="flex flex-col gap-1">
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={isSaving || !content.trim()}
                            onClick={handleSave}
                            className="h-8 w-8 p-0"
                        >
                            {isSaving ? <Loader className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={isSaving}
                            onClick={onCancel}
                            className="h-8 w-8 p-0"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                        </div>


                    </div>

                </div>
            </CardContent>
        </Card>
    );
}

interface NoteCardProps {
    note: ActivityWithCustomer;
    isEditing: boolean;
    onEdit: () => void;
    onCancelEdit: () => void;
    onSave: () => void;
    users: DbUserWithPackages[];
    showCustomerName?: boolean;
    activity_type?: "risk" | "sales_notes";
}

function NoteCard({ note, isEditing, onEdit, onCancelEdit, onSave, users, showCustomerName, activity_type }: NoteCardProps) {
    const [customerIds, setCustomerIds] = useQueryParamArray("customer_ids");
    const { member } = useStytchMember();
    const currentUserId = member ? getUserDbId(member) : "";

    if (isEditing) {
        return (
            <NoteForm
                customerId={note.customer_id}
                note={note}
                onSave={onSave}
                onCancel={onCancelEdit}
                currentUserId={currentUserId}
                allowCustomerSelection={false}
                activity_type={activity_type}
            />
        );
    }

    const createdBy = users.find(u => u.id === note.created_by_id)?.name || "Unknown";
    const updatedBy = users.find(u => u.id === note.last_updated_by_id)?.name || "Unknown";

    return (
        <Card>
            <CardContent className="p-4">
                <div className="space-y-3">
                    {showCustomerName && note.Customer?.long_name && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="font-semibold text-sm text-red-600"
                            onClick={() => setCustomerIds([note.customer_id])}
                        >
                            {note.Customer.long_name}
                        </Button>
                    )}

                    <div className="flex items-start justify-between gap-3">
                            <Textarea
                                value={note.content}
                                onChange={(e) => {}}
                                className="w-full"
                                readOnly
                                rows={9}
                            />
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onEdit}
                            className="h-8 w-8 p-0 flex-shrink-0"
                        >
                            <Edit2 className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Footer with timestamp */}
                    <div className="grid grid-cols-2 gap-2 md:gap-0 md:flex items-end justify-between text-xs text-gray-500">
                        <time className="grid">
                            <span>Created At</span>
                            <time className="flex-shrink-0" dateTime={moment.tz(note.created_at, CommonDestinationTimezone).format("YYYY-MM-DD")}>
                                {moment.tz(note.created_at, CommonDestinationTimezone).format("MMM Do YYYY")}
                            </time>
                        </time>

                        <div className="grid">
                            <span>Last Updated</span>
                            <time className="flex-shrink-0" dateTime={moment.tz(note.updated_at, CommonDestinationTimezone).format("YYYY-MM-DD")}>
                                {moment.tz(note.updated_at, CommonDestinationTimezone).format("MMM Do YYYY")}
                            </time>
                        </div>

                        <div className="grid">
                            <span>Created By:</span>
                            <span className="text-sm text-muted-foreground">
                                {createdBy}
                            </span>
                        </div>
                        <div className="grid">
                            <span>Last Updated By:</span>
                            <span className="text-sm text-muted-foreground">
                                {updatedBy}
                            </span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
